import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { WORK_ORDER_CREATE, WORK_ORDER_UPDATE, WORK_ORDER_GET_LIST_FILTER } from '../../graphql/workOrders';
import { EMPLOYEE_LIST } from '../../graphql/employees';
import { VEHICLES_LIST } from '../../graphql/vehicles';
import { INSPECTIONS_LIST } from '../../graphql/inspections';
import { 
  TextField, 
  Button, 
  MenuItem, 
  Box, 
  CircularProgress, 
  Paper, 
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import {
  Build,
  Assignment,
  Description,
  Category,
  CalendarToday,
  Schedule,
  Person,
  Notes,
  FactCheck,
  Link,
  Close
} from '@mui/icons-material';
import EditableFormField from '../../components/editable-form-field';
import dayjs from 'dayjs';
import DatePickerAuto from '../../components/custom-datepicker/DatePickerAuto';
import { useAuthContext } from '../../sections/auth/useAuthContext';

// Work order categories
const workOrderCategories = [
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'body_repair', label: 'Body Repair' },
  { value: 'tire_service', label: 'Tire Service' },
  { value: 'brake_service', label: 'Brake Service' },
  { value: 'engine_service', label: 'Engine Service' },
  { value: 'transmission', label: 'Transmission' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'exhaust', label: 'Exhaust System' },
  { value: 'cooling_system', label: 'Cooling System' },
  { value: 'fuel_system', label: 'Fuel System' },
  { value: 'interior', label: 'Interior' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' }
];

const statuses = ['Open', 'In Progress', 'Completed', 'Cancelled'];

// Mock user role - in real app, this would come from auth context
const getUserRole = () => {
  // This should be replaced with actual user role from authentication
  return 'technician'; // 'technician', 'non-technician', 'repair_tech'
};

const WorkOrderForm = ({ vehicleId, onClose, isEditing: externalIsEditing, onToggleEdit, isModal = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const userRole = getUserRole();
  
  const [internalIsEditing, setInternalIsEditing] = useState(!isEdit);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = onToggleEdit || setInternalIsEditing;

  const { data: employeesData, loading: employeesLoading } = useQuery(EMPLOYEE_LIST);
  const { data: vehiclesData, loading: vehiclesLoading } = useQuery(VEHICLES_LIST);
  const { data: inspectionsData, loading: inspectionsLoading } = useQuery(INSPECTIONS_LIST);
  const { data: workOrderData, loading: workOrderLoading } = useQuery(
    WORK_ORDER_GET_LIST_FILTER,
    { variables: { id }, skip: !isEdit }
  );

  const [createWorkOrder, { loading: creating }] = useMutation(WORK_ORDER_CREATE, {
    refetchQueries: ['VehicleGet'],
    onCompleted: () => {
      if (isModal && onClose) {
        onClose(true);
      } else if (!isModal && onClose) {
        onClose(true);
      } else if (!isModal) {
        navigate('/fleet/work-orders');
      }
    },
  });
  const [updateWorkOrder, { loading: updating }] = useMutation(WORK_ORDER_UPDATE, {
    refetchQueries: ['VehicleGet'],
    onCompleted: () => {
      if (isModal && onClose) {
        onClose(true);
      } else if (!isModal && onClose) {
        onClose(true);
      } else if (!isModal) {
        // Stay on the work order view after updating
        setIsEditing(false);
      }
    },
  });

  const { control, handleSubmit, reset, formState: { errors }, watch } = useForm({
    defaultValues: {
      title: '',
      description: '',
      category: '',
      status: 'Open', // Default status
      dateOpened: dayjs().format('YYYY-MM-DD'), // Default to today
      estimatedCompletionDate: '',
      assignedTo: '',
      workOrders: vehicleId || '', // Use passed vehicleId if available
      workOrderDetails: '',
      repairNotes: '',
      inspection: '', // Inspection reference
    },
  });

  useEffect(() => {
    if (isEdit && workOrderData?.workOrdersList?.items?.length) {
      const wo = workOrderData.workOrdersList.items[0];
      
      reset({
        title: wo.title || '',
        description: wo.description || '',
        category: wo.category || '',
        status: wo.status || 'Open',
        dateOpened: wo.dateOpened ? wo.dateOpened.split('T')[0] : dayjs().format('YYYY-MM-DD'),
        estimatedCompletionDate: wo.estimatedCompletionDate ? wo.estimatedCompletionDate.split('T')[0] : '',
        assignedTo: wo.assignedTo?.id || '',
        workOrders: wo.workOrders?.id || '',
        workOrderDetails: wo.workOrderDetails || '',
        repairNotes: wo.repairNotes || '',
        inspection: wo.inspection?.id || '',
      });
    }
  }, [isEdit, workOrderData, reset]);

  const onSubmit = async (values) => {
    const data = {
      title: values.title,
      description: values.description,
      category: values.category,
      status: values.status,
      dateOpened: values.dateOpened,
      estimatedCompletionDate: values.estimatedCompletionDate ? values.estimatedCompletionDate : null,
      assignedTo: values.assignedTo ? { connect: { id: values.assignedTo } } : undefined,
      workOrders: values.workOrders ? { connect: { id: values.workOrders } } : undefined,
      workOrderDetails: values.workOrderDetails,
      repairNotes: values.repairNotes,
      inspection: values.inspection ? { connect: { id: values.inspection } } : undefined,
    };
    
    if (isEdit) {
      await updateWorkOrder({ variables: { filter: { id }, data } });
    } else {
      await createWorkOrder({ variables: { data } });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isEdit) {
      const wo = workOrderData.workOrdersList.items[0];
      reset({
        title: wo.title || '',
        description: wo.description || '',
        category: wo.category || '',
        status: wo.status || 'Open',
        dateOpened: wo.dateOpened ? wo.dateOpened.split('T')[0] : dayjs().format('YYYY-MM-DD'),
        estimatedCompletionDate: wo.estimatedCompletionDate ? wo.estimatedCompletionDate.split('T')[0] : '',
        assignedTo: wo.assignedTo?.id || '',
        workOrders: wo.workOrders?.id || '',
        workOrderDetails: wo.workOrderDetails || '',
        repairNotes: wo.repairNotes || '',
        inspection: wo.inspection?.id || '',
      });
      setIsEditing(false);
    } else {
      if (onClose) onClose();
      else navigate('/fleet/work-orders');
    }
  };

  if (isEdit && workOrderLoading) return <CircularProgress />;

  const formValues = watch();
  
  const employeeOptions = employeesData?.employeesList?.items?.map(emp => ({
    value: emp.id,
    label: `${emp.firstName} ${emp.lastName}`
  })) || [];
  
  const vehicleOptions = vehiclesData?.vehiclesList?.items?.map(vehicle => ({
    value: vehicle.id,
    label: `${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`
  })) || [];
  
  const inspectionOptions = inspectionsData?.inspectionsList?.items?.map(inspection => ({
    value: inspection.id,
    label: `${inspection.title || 'Inspection'} - ${inspection.inspector || 'Unknown'} (${inspection.date ? dayjs(inspection.date).format('MMM DD, YYYY') : 'No date'})`
  })) || [];

  // Calculate days open
  const daysOpen = formValues.dateOpened ? dayjs().diff(dayjs(formValues.dateOpened), 'day') : 0;

  // Determine field visibility based on user role
  const showTechnicianFields = userRole === 'technician';
  const isRepairTech = userRole === 'repair_tech';
  const isNonTechnician = userRole === 'non-technician';

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#ef4444', mr: 2 }}>
            <Build />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {isEdit ? (isEditing ? 'Edit' : 'View') : 'Create'} Work Order
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isEdit ? 'Update work order details' : 'Create a new work order'}
            </Typography>
          </Box>
        </Box>
        
        {/* Action Buttons for View Mode - Top */}
        {isEdit && !isEditing && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              onClick={() => navigate('/fleet/work-orders')}
              variant="outlined"
              startIcon={<Close />}
            >
              Close
            </Button>
            <Button 
              onClick={handleEdit}
              variant="contained"
              color="primary"
              sx={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                }
              }}
            >
              Edit
            </Button>
          </Box>
        )}
        
      </Box>

      {isEditing || !isEdit ? (
        <form onSubmit={(e) => {
          e.stopPropagation(); // Prevent event from bubbling up to parent forms
          handleSubmit(onSubmit)(e);
        }}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assignment sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Basic Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={12}>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Title is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField 
                    {...field} 
                    label="Title (Problem Summary)" 
                    fullWidth 
                    margin="normal" 
                    disabled={!isEditing || isRepairTech}
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <Description sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="workOrders"
                control={control}
                rules={{ required: 'Vehicle is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField 
                    {...field} 
                    select 
                    label="Vehicle" 
                    fullWidth 
                    margin="normal" 
                    disabled={!isEditing || isRepairTech}
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <Build sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                  >
                    {vehicleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField 
                    {...field} 
                    select 
                    label="Category" 
                    fullWidth 
                    margin="normal" 
                    disabled={!isEditing || isRepairTech}
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <Category sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                  >
                    {workOrderCategories.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{ required: 'Description is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField 
                    {...field} 
                    label="Description (Detailed Explanation)" 
                    fullWidth 
                    margin="normal" 
                    multiline 
                    sx={{height: '202px', "& .MuiInputBase-root": {height: '100%'}, "& .MuiInputBase-input": {height: '169px !important',overflow: 'auto !important'}}}
                    disabled={!isEditing || isRepairTech}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="dateOpened"
                control={control}
                rules={{ required: 'Date Opened is required' }}
                                  render={({ field, fieldState: { error } }) => (
                    <DatePickerAuto
                      editTab={isEditing}
                      rest={{}}
                      value={field.value}
                      sx={{width: '100%', mt: "16px", mb: "8px"}}
                      label="Date Opened"
                      onUpdate={(date) => {
                        field.onChange(date);
                      }}
                      required
                    />
                  )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField 
                label="Days Open" 
                value={`${daysOpen} days`}
                fullWidth 
                margin="normal" 
                disabled
                InputProps={{
                  startAdornment: (
                    <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    color: daysOpen > 30 ? '#ef4444' : daysOpen > 14 ? '#f59e0b' : '#10b981',
                    fontWeight: 600
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="inspection"
                control={control}
                render={({ field }) => (
                  <TextField 
                    {...field} 
                    select 
                    label="Created from Inspection (Optional)" 
                    fullWidth 
                    margin="normal" 
                    disabled={!isEditing || isRepairTech}
                    InputProps={{
                      startAdornment: (
                        <FactCheck sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    helperText="Select an inspection if this work order was created from an inspection"
                  >
                    <MenuItem value="">No inspection reference</MenuItem>
                    {inspectionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Work Order Details */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
                <Notes sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Work Order Note
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sx={{pt:"0 !important"}}>
              <Controller
                name="workOrderDetails"
                control={control}
                render={({ field }) => (
                  <TextField 
                    {...field} 
                    label="Work Order Note (Problem Notes from Inspection)" 
                    fullWidth 
                    margin="normal" 
                    multiline 
                    sx={{height: '202px', "& .MuiInputBase-root": {height: '100%'}, "& .MuiInputBase-input": {height: '169px !important',overflow: 'auto !important'}}}
                    disabled={!isEditing || isRepairTech}
                    placeholder="Enter problem notes passed from inspection..."
                  />
                )}
              />
            </Grid>

            {/* Technician Fields - Only visible to technicians */}
            {showTechnicianFields && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Technician Information
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField 
                        {...field} 
                        select 
                        label="Status" 
                        fullWidth 
                        margin="normal" 
                        disabled={!isEditing}
                      >
                        {statuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            <Chip 
                              label={status} 
                              size="small" 
                              color={
                                status === 'Completed' ? 'success' : 
                                status === 'In Progress' ? 'warning' : 
                                status === 'Cancelled' ? 'error' : 'default'
                              }
                            />
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="estimatedCompletionDate"
                    control={control}
                    render={({ field }) => (
                      <DatePickerAuto
                        editTab={isEditing}
                        rest={{}}
                        value={field.value}
                        label="Estimated Completion Date"
                        onUpdate={(date) => {
                          field.onChange(date);
                        }}
                        sx={{width: '100%', mt: "16px", mb: "8px"}}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="assignedTo"
                    control={control}
                    render={({ field }) => (
                      <TextField 
                        {...field} 
                        select 
                        label="Assigned To" 
                        fullWidth 
                        margin="normal" 
                        disabled={!isEditing}
                        InputProps={{
                          startAdornment: (
                            <Person sx={{ mr: 1, color: 'text.secondary' }} />
                          ),
                        }}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {employeeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Repair Tech Fields - Only visible to repair techs */}
            {isRepairTech && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Build sx={{ mr: 1, color: 'error.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Repair Information
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField 
                        {...field} 
                        select 
                        label="Status" 
                        fullWidth 
                        margin="normal" 
                        disabled={!isEditing}
                      >
                        {statuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            <Chip 
                              label={status} 
                              size="small" 
                              color={
                                status === 'Completed' ? 'success' : 
                                status === 'In Progress' ? 'warning' : 
                                status === 'Cancelled' ? 'error' : 'default'
                              }
                            />
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="repairNotes"
                    control={control}
                    render={({ field }) => (
                      <TextField 
                        {...field} 
                        label="Repair Notes" 
                        fullWidth 
                        margin="normal" 
                        multiline 
                        rows={3}
                        disabled={!isEditing}
                        placeholder="Enter repair notes and findings..."
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Action Buttons - Bottom */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                <Button 
                  onClick={handleCancel} 
                  variant="outlined"
                  disabled={creating || updating}
                >
                  {isEdit ? 'Cancel' : 'Close'}
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={creating || updating}
                  sx={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                    }
                  }}
                >
                  {creating || updating ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    isEdit ? 'Update Work Order' : 'Create Work Order'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      ) : (
        // View Mode
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Work Order Details
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Typography variant="caption" color="text.secondary">Title</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {formValues.title || 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Vehicle</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {(() => {
                const found = vehicleOptions.find(vehicle => vehicle.value === formValues.workOrders);
                return found?.label || 'Not specified';
              })()}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {workOrderCategories.find(cat => cat.value === formValues.category)?.label || 'Not specified'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {formValues.description || 'Not specified'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Date Opened</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
              {formValues.dateOpened ? dayjs(formValues.dateOpened).format('MMM DD, YYYY') : 'Not specified'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="text.secondary">Days Open</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, color: daysOpen > 30 ? '#ef4444' : daysOpen > 14 ? '#f59e0b' : '#10b981' }}>
              {daysOpen} days
            </Typography>
          </Grid>

          {formValues.inspection && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Link sx={{ mr: 1, color: 'primary.main', fontSize: 16 }} />
                <Typography variant="caption" color="text.secondary">Created from Inspection</Typography>
              </Box>
              <Chip
                icon={<FactCheck sx={{ fontSize: 16 }} />}
                label={(() => {
                  const found = inspectionOptions.find(inspection => inspection.value === formValues.inspection);
                  return found?.label || 'Inspection Reference';
                })()}
                color="info"
                variant="outlined"
                clickable
                onClick={() => navigate(`/fleet/inspections/${formValues.inspection}`)}
                sx={{ 
                  fontWeight: 500,
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  '& .MuiChip-icon': {
                    color: '#3b82f6'
                  }
                }}
              />
            </Grid>
          )}

          {formValues.workOrderDetails && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Work Order Details</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                {formValues.workOrderDetails}
              </Typography>
            </Grid>
          )}

          {showTechnicianFields && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Technician Information
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Chip 
                  label={formValues.status || 'Open'} 
                  color={
                    formValues.status === 'Completed' ? 'success' : 
                    formValues.status === 'In Progress' ? 'warning' : 
                    formValues.status === 'Cancelled' ? 'error' : 'default'
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                  {(() => {
                    const found = employeeOptions.find(emp => emp.value === formValues.assignedTo);
                    return found?.label || 'Unassigned';
                  })()}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Estimated Completion</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                  {formValues.estimatedCompletionDate ? dayjs(formValues.estimatedCompletionDate).format('MMM DD, YYYY') : 'Not set'}
                </Typography>
              </Grid>
            </>
          )}

          {isRepairTech && formValues.repairNotes && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Build sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Repair Information
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Repair Notes</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
                {formValues.repairNotes}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

    </Paper>
  );
};

export default WorkOrderForm; 